// src/pages/Home/HomePage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent,
  Fade, Avatar, Chip, Stack, GlobalStyles, useMediaQuery, useTheme,
  Paper, Divider, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowForward, Hub, RocketLaunch, Verified,
  Business, TrendingUp, Security, Speed,
  Storefront, Lightbulb, Groups, WorkspacePremium,
  MonetizationOn, PhoneAndroid, Web, ConnectWithoutContact,
  CheckCircle, TrendingDown, Timeline
} from '@mui/icons-material';

const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.08)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%)',
};

const heroContent = [
  {
    tag: 'CONNECT',
    title: 'Where Problems Meet Solutions',
    subtitle: 'Rengigs connects forward-thinking organizations with verified suppliers across major sectors. Post challenges, find solutions, and grow together.',
    stat1: '10K+',
    stat1Label: 'ORGANIZATIONS',
    stat2: '5K+',
    stat2Label: 'VERIFIED SUPPLIERS',
    image: '/assets/images/hero8.jpg',
    highlight: 'Bridging Innovation Across Industries',
  },
  {
    tag: 'COLLABORATE',
    title: 'Share Problems, Find Experts',
    subtitle: 'Organizations share real-world challenges. Industry experts and vendors provide tailored solutions. Collaborative problem-solving at scale.',
    stat1: '50K+',
    stat1Label: 'PROBLEMS SOLVED',
    stat2: '95%',
    stat2Label: 'SATISFACTION RATE',
    image: '/assets/images/hero9.jpg',
    highlight: 'Expert Solutions from Global Network',
  },
  {
    tag: 'MONETIZE',
    title: 'List Solutions, Scale Business',
    subtitle: 'Suppliers and vendors list premium solutions. Organizations subscribe to what they need. Monetize your expertise and grow your business.',
    stat1: '100+',
    stat1Label: 'INDUSTRIES',
    stat2: '24/7',
    stat2Label: 'GLOBAL ACCESS',
    image: '/assets/images/hero10.jpg',
    highlight: 'Premium Subscriptions, Unlimited Growth',
  },
];

// Core value propositions
const valueProps = [
  {
    icon: <Storefront />,
    title: 'For Organizations & Startups',
    description: 'Post problems, discover verified suppliers, and access premium solutions tailored to your needs. Scale your operations with trusted partners.',
    benefits: ['Post unlimited challenges', 'AI-matched solutions', 'Track progress', 'Secure payments'],
    color: rengigsPalette.forestGreen,
    gradient: rengigsPalette.gradient,
  },
  {
    icon: <Lightbulb />,
    title: 'For Suppliers & Vendors',
    description: 'Showcase your expertise, list premium solutions, and connect with organizations seeking your services. Monetize your solutions with subscription models.',
    benefits: ['List unlimited solutions', 'Receive qualified leads', 'Analytics dashboard', 'Subscription revenue'],
    color: rengigsPalette.mediumGreen,
    gradient: rengigsPalette.gradient,
  },
  {
    icon: <WorkspacePremium />,
    title: 'Premium Features',
    description: 'Unlock advanced capabilities with premium subscriptions. Both organizations and suppliers can access enhanced tools and priority support.',
    benefits: ['Priority matching', 'Advanced analytics', 'Dedicated support', 'Custom integrations'],
    color: rengigsPalette.brandLime,
    gradient: rengigsPalette.gradient,
  },
];

// Platform features
const platformFeatures = [
  {
    icon: <ConnectWithoutContact />,
    title: 'Problem Marketplace',
    description: 'Organizations share challenges. Suppliers respond with solutions.',
    color: rengigsPalette.forestGreen,
  },
  {
    icon: <Storefront />,
    title: 'Solution Listings',
    description: 'Vendors list premium solutions. Organizations subscribe to what they need.',
    color: rengigsPalette.mediumGreen,
  },
  {
    icon: <MonetizationOn />,
    title: 'Premium Monetization',
    description: 'Both vendors and organizations can upgrade for enhanced features and capabilities.',
    color: rengigsPalette.lightGreen,
  },
  {
    icon: <PhoneAndroid />,
    title: 'Mobile & Web',
    description: 'Access the platform anywhere, anytime. Seamless experience across all devices.',
    color: rengigsPalette.brandLime,
  },
  {
    icon: <Groups />,
    title: 'Cross-Sector Connection',
    description: 'Connect across major industries including Tech, Healthcare, Manufacturing, and more.',
    color: rengigsPalette.forestGreen,
  },
  {
    icon: <Verified />,
    title: 'Verified Trust Network',
    description: 'All organizations and suppliers are vetted for trust and quality assurance.',
    color: rengigsPalette.mediumGreen,
  },
];

// Industries served
const industries = [
  'Technology', 'Healthcare', 'Manufacturing', 'Finance',
  'Retail', 'Logistics', 'Energy', 'Education',
  'Agriculture', 'Construction', 'Hospitality', 'Automotive'
];

// Global scrollbar styles
const scrollbarStyles = {
  '*::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '*::-webkit-scrollbar-track': {
    background: rengigsPalette.offWhite,
    borderRadius: '10px',
  },
  '*::-webkit-scrollbar-thumb': {
    background: `linear-gradient(135deg, ${rengigsPalette.forestGreen}, ${rengigsPalette.mediumGreen})`,
    borderRadius: '10px',
    transition: 'background 0.3s ease',
  },
  '*::-webkit-scrollbar-thumb:hover': {
    background: `linear-gradient(135deg, ${rengigsPalette.deepForest}, ${rengigsPalette.lightGreen})`,
  },
  '*': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}`,
  },
};

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      
      <Box sx={{ bgcolor: rengigsPalette.offWhite, minHeight: '100vh', overflowX: 'hidden' }}>
        
        {/* --- HERO SECTION --- */}
        <Box sx={{ pt: { xs: 4, md: 12 }, pb: { xs: 8, md: 15 }, position: 'relative', overflow: 'hidden' }}>
          {/* Decorative background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${rengigsPalette.forestGreen}10, transparent)`,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -100,
              left: -100,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${rengigsPalette.brandLime}10, transparent)`,
              zIndex: 0,
            }}
          />
          
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={8} alignItems="center">
              
              {/* Left Content Column */}
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Fade in key={currentSlide} timeout={1000}>
                    <Box>
                      <Chip 
                        label={heroContent[currentSlide].tag}
                        sx={{ mb: 3, bgcolor: rengigsPalette.softGreen, color: rengigsPalette.forestGreen, fontWeight: 700 }} 
                      />
                      
                      <Typography 
                        variant="h1" 
                        sx={{ 
                          fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' }, 
                          fontWeight: 900, 
                          lineHeight: 1.1,
                          mb: 2,
                          background: rengigsPalette.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {heroContent[currentSlide].title}
                      </Typography>

                      <Typography 
                        variant="h5" 
                        sx={{ color: rengigsPalette.charcoal, mb: 2, lineHeight: 1.5, opacity: 0.85, fontWeight: 500 }}
                      >
                        {heroContent[currentSlide].subtitle}
                      </Typography>

                      <Typography 
                        variant="body1" 
                        sx={{ color: rengigsPalette.mediumGreen, mb: 4, fontWeight: 600 }}
                      >
                        {heroContent[currentSlide].highlight}
                      </Typography>
                    </Box>
                  </Fade>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/register')}
                      sx={{
                        background: rengigsPalette.gradient,
                        backgroundSize: '200% auto',
                        color: rengigsPalette.white,
                        px: 5, py: 1.8,
                        borderRadius: '12px',
                        fontWeight: 700,
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          background: rengigsPalette.gradientReverse,
                          backgroundSize: '200% auto',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                        }
                      }}
                    >
                      Join as Organization
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/solutions')}
                      sx={{
                        border: `2px solid ${rengigsPalette.forestGreen}`,
                        color: rengigsPalette.forestGreen,
                        px: 4,
                        borderRadius: '12px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          border: '2px solid transparent',
                          background: rengigsPalette.gradient,
                          backgroundSize: '200% auto',
                          color: rengigsPalette.white,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                        }
                      }}
                    >
                      Join as Supplier
                    </Button>
                  </Stack>

                  <Box sx={{ display: 'flex', gap: { xs: 4, md: 6 }, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 900, color: rengigsPalette.forestGreen }}>
                        {heroContent[currentSlide].stat1}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: rengigsPalette.deepForest, fontWeight: 700 }}>
                        {heroContent[currentSlide].stat1Label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 900, color: rengigsPalette.forestGreen }}>
                        {heroContent[currentSlide].stat2}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: rengigsPalette.deepForest, fontWeight: 700 }}>
                        {heroContent[currentSlide].stat2Label}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Right Image Column */}
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <Fade in key={currentSlide} timeout={1200}>
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '796 / 602', 
                        borderRadius: '32px',
                        boxShadow: `0 40px 80px -20px ${rengigsPalette.forestGreen}30`,
                        border: `10px solid ${rengigsPalette.white}`,
                        overflow: 'hidden',
                        bgcolor: rengigsPalette.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                          '0%': { transform: 'translateY(0px)' },
                          '50%': { transform: 'translateY(-20px)' },
                          '100%': { transform: 'translateY(0px)' },
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={heroContent[currentSlide].image}
                        alt="Rengigs Ecosystem"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Fade>
                  
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: '100%',
                      height: '100%',
                      background: rengigsPalette.gradient,
                      borderRadius: '32px',
                      zIndex: -1,
                      opacity: 0.1,
                      transform: 'rotate(3deg)'
                    }}
                  />
                </Box>
              </Grid>

            </Grid>
          </Container>
        </Box>

        {/* --- CORE VALUE PROPOSITIONS --- */}
        <Box sx={{ py: 10, bgcolor: rengigsPalette.white }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="THE RENGIGS ECOSYSTEM"
                sx={{
                  mb: 2,
                  bgcolor: rengigsPalette.softGreen,
                  color: rengigsPalette.forestGreen,
                  fontWeight: 700,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  color: rengigsPalette.charcoal,
                  mb: 2,
                }}
              >
                Connect. Share. Solve. Grow.
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: rengigsPalette.mediumGreen, maxWidth: 800, mx: 'auto' }}
              >
                A complete platform where organizations share challenges and suppliers provide solutions
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {valueProps.map((prop, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 3, height: '100%', borderRadius: '24px', 
                      border: `1px solid ${rengigsPalette.border}`,
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        borderColor: prop.color,
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px -10px ${prop.color}40`,
                      }
                    }}
                  >
                    <CardContent>
                      <Avatar sx={{ 
                        bgcolor: rengigsPalette.softGreen, 
                        color: prop.color, 
                        mb: 3, 
                        width: 64, 
                        height: 64,
                        background: `linear-gradient(135deg, ${prop.color}20, ${rengigsPalette.white})`,
                      }}>
                        {prop.icon}
                      </Avatar>
                      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: prop.color }}>
                        {prop.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#5F6D7C', lineHeight: 1.6, mb: 2 }}>
                        {prop.description}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Stack spacing={1}>
                        {prop.benefits.map((benefit, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle sx={{ fontSize: 16, color: prop.color }} />
                            <Typography variant="body2">{benefit}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* --- PLATFORM FEATURES --- */}
        <Box sx={{ py: 10, bgcolor: rengigsPalette.offWhite }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="PLATFORM CAPABILITIES"
                sx={{
                  mb: 2,
                  bgcolor: rengigsPalette.softGreen,
                  color: rengigsPalette.forestGreen,
                  fontWeight: 700,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  color: rengigsPalette.charcoal,
                  mb: 2,
                }}
              >
                Everything You Need in One Place
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {platformFeatures.map((feature, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: '20px',
                      bgcolor: rengigsPalette.white,
                      border: `1px solid ${rengigsPalette.border}`,
                      transition: 'all 0.3s ease',
                      textAlign: 'center',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: feature.color,
                        boxShadow: `0 10px 20px -5px ${feature.color}30`,
                      }
                    }}
                  >
                    <Avatar sx={{ 
                      mx: 'auto', 
                      mb: 2, 
                      bgcolor: rengigsPalette.softGreen, 
                      color: feature.color,
                      width: 56,
                      height: 56,
                    }}>
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5F6D7C' }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* --- INDUSTRIES SERVED --- */}
        <Box sx={{ py: 10, bgcolor: rengigsPalette.white }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="INDUSTRIES WE SERVE"
                sx={{
                  mb: 2,
                  bgcolor: rengigsPalette.softGreen,
                  color: rengigsPalette.forestGreen,
                  fontWeight: 700,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  color: rengigsPalette.charcoal,
                  mb: 2,
                }}
              >
                Connecting Across Major Sectors
              </Typography>
              <Typography variant="body1" sx={{ color: '#5F6D7C', maxWidth: 700, mx: 'auto' }}>
                From Technology to Healthcare, Manufacturing to Finance – Rengigs bridges innovation across industries
              </Typography>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              {industries.map((industry, i) => (
                <Grid item key={i}>
                  <Chip
                    label={industry}
                    sx={{
                      bgcolor: rengigsPalette.softGreen,
                      color: rengigsPalette.forestGreen,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: rengigsPalette.forestGreen,
                        color: rengigsPalette.white,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* --- PREMIUM MONETIZATION SECTION --- */}
        <Box
          sx={{
            py: 10,
            background: rengigsPalette.gradient,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Chip
              label="PREMIUM FEATURES"
              sx={{
                mb: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 700,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 800,
                mb: 2,
                color: 'white',
              }}
            >
              Unlock Premium Capabilities
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              Both organizations and suppliers can upgrade to premium for enhanced features,
              priority support, and advanced analytics
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  background: rengigsPalette.gradient,
                  backgroundSize: '200% auto',
                  color: rengigsPalette.white,
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: rengigsPalette.gradientReverse,
                    backgroundSize: '200% auto',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                  },
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/solutions')}
                sx={{
                  border: `2px solid ${rengigsPalette.white}`,
                  color: rengigsPalette.white,
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    border: '2px solid transparent',
                    background: rengigsPalette.gradient,
                    backgroundSize: '200% auto',
                    color: rengigsPalette.white,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                  },
                }}
              >
                View Premium Plans
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* --- CTA SECTION --- */}
        <Box sx={{ py: 8, bgcolor: rengigsPalette.white }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontWeight: 700,
                color: rengigsPalette.charcoal,
                mb: 2,
              }}
            >
              Ready to Join the Rengigs Ecosystem?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: '#5F6D7C',
                fontSize: '1.1rem',
              }}
            >
              Whether you're an organization seeking solutions or a supplier offering expertise,
              Rengigs connects you with the right partners
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  background: rengigsPalette.gradient,
                  backgroundSize: '200% auto',
                  color: rengigsPalette.white,
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    background: rengigsPalette.gradientReverse,
                    backgroundSize: '200% auto',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                  }
                }}
              >
                Get Started Today
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/solutions')}
                sx={{
                  border: `2px solid ${rengigsPalette.forestGreen}`,
                  color: rengigsPalette.forestGreen,
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '2px solid transparent',
                    background: rengigsPalette.gradient,
                    backgroundSize: '200% auto',
                    color: rengigsPalette.white,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 15px 25px -10px ${rengigsPalette.forestGreen}80`,
                  },
                }}
              >
                Browse Solutions
              </Button>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default HomePage;